import { Suspense } from "react"
import { Navigate, Route, Routes as RouterRoutes, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"

import { selectCurrentUser, selectTerms } from "features/store/authSlice"
import { DEFAULT_TARGET, PREVIOUS_LOCATION_KEY } from "helpers/utils/constants"
import { lazyWithReload } from "helpers/utils/staleChunk"
import { termsCheckByStatus } from "helpers/utils/user"
import { DashboardLayout } from "layouts/DashboardLayout"
import { PublicLayout } from "layouts/PublicLayout"
import DeviceData from "pages/orgs/Device/DeviceData"
import DeviceAmbient from "pages/orgs/Device/DeviceAmbient"
import MainDashboard from "pages/orgs/Device/MainDashboard"
import Login from "pages/accounts/Login"
import LoginOAuth from "pages/accounts/LoginOAuth"
import Logout from "pages/accounts/Logout"
import Register from "pages/accounts/Register"
import Device from "pages/orgs/Device"
import DeviceAlerts from "pages/orgs/Device/DeviceAlerts"
import DeviceGroup from "pages/orgs/DeviceGroup"
import DeviceGroups from "pages/orgs/DeviceGroups"
import DeviceSettings from "pages/orgs/Device/DeviceSettings"
import GroupAlerts from "pages/orgs/DeviceGroup/GroupAlerts"
import GroupInfo from "pages/orgs/DeviceGroup/GroupInfo"
import OrgSettings from "pages/orgs/OrgSettings"
import OrgHomepage from "pages/orgs"
import UserHomepage from "pages/users"
import CreateToken from "pages/users/CreateToken"
import Api from "pages/documentation/api"
import ApiDoc from "pages/documentation/api/ApiDoc"
import BoardLoader from "pages/BoardLoader"
import PasswordRecover from "pages/accounts/PasswordRecover"
import ActivateUser from "pages/accounts/ActivateUser"
import AlertsTab from "pages/orgs/Alerts/AlertsTab"
import ActivityTab from "pages/orgs/Alerts/ActivityTab"
import Alerts from "pages/orgs/Alerts"
import AccountGeneral from "pages/users/AccountSettings/AccountGeneral"
import AccountSecurity from "pages/users/AccountSettings/AccountSecurity"
import AccountSettings from "pages/users/AccountSettings"
import ProductDocs from "pages/documentation/product"
import AuditTrail from "pages/orgs/AuditTrail"
import Map from "pages/orgs/Map"
import TermsAndConditions from "pages/public/TermsAndConditions"
import AcceptTermsAndConditions from "pages/users/AcceptTermsAndConditions"
import Powerlines from "pages/orgs/Powerlines"
import Powerline from "pages/orgs/Powerline"
import PowerlineInfo from "pages/orgs/Powerline/PowerlineInfo"
import PowerlineAssets from "pages/orgs/Powerline/PowerlineAssets"
import PowerlineSettings from "pages/orgs/Powerline/PowerlineSettings"
import PowerlineAlerts from "pages/orgs/Powerline/PowerlineAlerts"
import Preloader from "widgets/common/PreLoader"
import Tower from "pages/orgs/Tower"
import TowerInfo from "pages/orgs/Tower/TowerInfo"
import TowerDevices from "pages/orgs/Tower/TowerDevices"
import TowerSegments from "pages/orgs/Tower/TowerSegments"
import TowerAmbient from "pages/orgs/Tower/TowerAmbient"
import Changelog from "pages/changelog"
import TowerAlerts from "pages/orgs/Tower/TowerAlerts"
import TowerSettings from "pages/orgs/Tower/TowerSettings"
import Span from "pages/orgs/Span"
import SpanOverview from "pages/orgs/Span/SpanOverview"
import SpanSettings from "pages/orgs/Span/SpanSettings"
import { selectCurrentOrgUserRole } from "./store/orgSlice"

const BaseLayout = lazyWithReload(() =>
  import("layouts").then((module) => ({
    default: module.BaseLayout,
  })),
)
const RecoverPasswordLazy = lazyWithReload(() => import("pages/accounts/Recover"))
const ModifyPasswordLazy = lazyWithReload(() => import("pages/users/ModifyPassword"))
const QRPageLazy = lazyWithReload(() => import("pages/QRPage"))

export default function Routes() {
  const username = useSelector(selectCurrentUser)
  const currentOrgUserRole = useSelector(selectCurrentOrgUserRole)
  const isAdminOrOwner = currentOrgUserRole === "admin" || currentOrgUserRole === "owner"
  const terms = useSelector(selectTerms)

  const location = useLocation()

  if (
    !location.pathname.includes("/accounts/") &&
    !location.pathname.includes("terms-and-conditions") &&
    location.pathname !== DEFAULT_TARGET
  ) {
    sessionStorage.setItem(
      PREVIOUS_LOCATION_KEY,
      `${location.pathname}${location.search}`,
    )
  }

  return (
    <RouterRoutes>
      <Route element={<PublicLayout />}>
        <Route path="/accounts/login" element={<Login />} />
        <Route path="/accounts/logout" element={<Logout />} />
        <Route path="/accounts/oauth-login" element={<LoginOAuth />} />
        <Route path="/accounts/register" element={<Register />} />
        <Route
          path="/accounts/recover"
          element={
            <Suspense fallback={<Preloader />}>
              <RecoverPasswordLazy />
            </Suspense>
          }
        />
        <Route
          path="/accounts/:username/password-recover/:code"
          element={<PasswordRecover />}
        />
        <Route path="/accounts/:username/activate/:secret" element={<ActivateUser />} />
      </Route>

      <Route
        path="/qr"
        element={
          <Suspense fallback={<Preloader />}>
            <QRPageLazy />
          </Suspense>
        }
      />

      <Route path="/users/:username/modify-password" element={<PublicLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<Preloader />}>
              <ModifyPasswordLazy />
            </Suspense>
          }
        />
      </Route>
      {username && !termsCheckByStatus(terms) && (
        <Route
          path="/accept-terms-and-conditions"
          element={<AcceptTermsAndConditions />}
        />
      )}
      <Route path="terms-and-conditions" element={<TermsAndConditions />} />
      {username && <Route path="changelog" element={<Changelog />} />}

      <Route
        path="/"
        element={username ? <BoardLoader /> : <Navigate to="/accounts/login" />}
      />

      <Route
        element={
          !username ? (
            <Navigate to="/accounts/login" />
          ) : (
            <Suspense fallback={<Preloader />}>
              <BaseLayout />
            </Suspense>
          )
        }
      >
        <Route element={<DashboardLayout />}>
          <Route path="/users/:username">
            <Route index element={<UserHomepage />} />
            <Route path="settings" element={<AccountSettings />}>
              <Route index element={<AccountGeneral />} />
              <Route path="security" element={<AccountSecurity />} />
            </Route>
          </Route>

          <Route path="/orgs/:orgId">
            <Route index element={<OrgHomepage />} />
            <Route path="powerlines" element={<Powerlines />} />
            <Route path="powerlines/:id" element={<Powerline />}>
              <Route index element={<PowerlineInfo />} />
              <Route path="assets" element={<PowerlineAssets />} />
              <Route path="alerts" element={<PowerlineAlerts />} />
              <Route
                path="events-log"
                element={
                  <Navigate
                    to={{ pathname: "../alerts", search: "?view=activity" }}
                    replace
                  />
                }
              />
              <Route path="settings" element={<PowerlineSettings />} />
            </Route>
            <Route path="towers/:id" element={<Tower />}>
              <Route index element={<TowerInfo />} />
              <Route path="segments" element={<TowerSegments />} />
              <Route path="devices" element={<TowerDevices />} />
              <Route path="ambient" element={<TowerAmbient />} />
              <Route path="alerts" element={<TowerAlerts />} />
              <Route path="settings" element={<TowerSettings />} />
            </Route>
            <Route path="spans/:id" element={<Span />}>
              <Route index element={<SpanOverview />} />
              <Route path="settings" element={<SpanSettings />} />
            </Route>
            <Route path="device-groups" element={<DeviceGroups />} />
            <Route path="device-groups/:id" element={<DeviceGroup />}>
              <Route index element={<GroupInfo />} />
              <Route path="alerts" element={<GroupAlerts />} />
              <Route
                path="events-log"
                element={
                  <Navigate
                    to={{ pathname: "../alerts", search: "?view=activity" }}
                    replace
                  />
                }
              />
            </Route>
            <Route path="devices/:id" element={<Device />}>
              <Route index element={<MainDashboard />} />
              <Route path="device-data" element={<DeviceData />} />
              <Route path="ambient" element={<DeviceAmbient />} />
              <Route path="alerts" element={<DeviceAlerts />} />
              <Route
                path="events-log"
                element={
                  <Navigate
                    to={{ pathname: "../alerts", search: "?view=activity" }}
                    replace
                  />
                }
              />
              <Route path="settings" element={<DeviceSettings />} />
            </Route>
            <Route path="alerts" element={<Alerts />}>
              <Route index element={<AlertsTab />} />
              <Route path="activity" element={<ActivityTab />} />
              {/* Keeps links saved before the events log became the activity tab working */}
              <Route path="events-log" element={<Navigate to="../activity" replace />} />
            </Route>
            {isAdminOrOwner && (
              <Route path="administration/organization" element={<OrgSettings />} />
            )}
            {isAdminOrOwner && (
              <Route path="administration/audit-trail" element={<AuditTrail />} />
            )}
          </Route>
          <Route path="documentation/api" element={<Api />}>
            <Route index element={<ApiDoc />} />
            <Route path="create-token" element={<CreateToken />} />
          </Route>
          <Route path="documentation/product" element={<ProductDocs />} />
        </Route>
        <Route element={<DashboardLayout fullWidth />}>
          <Route path="/orgs/:orgId">
            <Route path="map" element={<Map />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to={DEFAULT_TARGET} replace />} />
    </RouterRoutes>
  )
}
